import React, { useEffect, useState } from "react";
import Modal from "../Modal";
import api from "../../utils/api";
import Menu from "../Menu";

type AllowedRole = "Operator" | "SuperAdmin";

type AddUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (userId: string, email?: string, role?: AllowedRole) => void;
  title?: string;
  allowedRoles?: AllowedRole[];
  hideTempPasswordField?: boolean;
  hideRoleSelector?: boolean;
  description?: React.ReactNode;
};

const generateTempPassword = () => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numbers = "0123456789";
  let password = "";

  for (let i = 0; i < 3; i += 1) {
    password += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  for (let i = 0; i < 3; i += 1) {
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
  }
  return password
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
};

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  title = "Create User",
  allowedRoles = ["Operator", "SuperAdmin"],
  hideTempPasswordField = false,
  hideRoleSelector = false,
  description,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(generateTempPassword());
  const [phone, setPhone] = useState("");
  const firstAllowedRole = (allowedRoles[0] ?? "Operator") as AllowedRole;
  const firstAllowedRoleLabel = firstAllowedRole === "SuperAdmin" ? "Super Admin" : "Operator";
  const [role, setRole] = useState<AllowedRole>(firstAllowedRole);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (isOpen) {
      setName("");
      setEmail("");
      setPhone("");
      setRole((allowedRoles[0] ?? "Operator") as AllowedRole);
      setError(null);
      setPassword(generateTempPassword());
      setIsSubmitting(false);
      setFieldErrors({});
    }
  }, [isOpen, allowedRoles.length]);

  const normalizePhone = (value: string) => value.replace(/[^0-9A-Za-z]/g, "");

  const formatPhone = (value: string) => {
    const cleaned = normalizePhone(value).toUpperCase().slice(0, 10);
    const part1 = cleaned.slice(0, 3);
    const part2 = cleaned.slice(3, 6);
    const part3 = cleaned.slice(6, 10);
    const formatted = [part1, part2, part3].filter(Boolean).join("-");
    return { cleaned, formatted };
  };

  const handlePhoneChange = (value: string) => {
    const { formatted } = formatPhone(value);
    setPhone(formatted);
  };

  const validateFields = () => {
    const errors: Record<string, string | null> = {};
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const normalizedPhone = normalizePhone(phone);

    if (!trimmedName) {
      errors.name = "Full name is required.";
    } else if (trimmedName.length > 60) {
      errors.name = "Full name must be 60 characters or less.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(trimmedEmail)) {
      errors.email = "Enter a valid email.";
    }

    if (!hideTempPasswordField) {
      if (!password.trim()) {
        errors.password = "Temporary password is required.";
      } else if (password.length < 6) {
        errors.password = "Password must be at least 6 characters.";
      }
    }

    if (normalizedPhone && normalizedPhone.length !== 10) {
      errors.phone = "Enter a 10 character phone number.";
    }

    setFieldErrors(errors);
    return Object.values(errors).some(Boolean);
  };

  const handleSubmit = async () => {
    if (validateFields()) {
      setError("Please fix the highlighted fields.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const trimmedName = name.trim();
      const cleanEmail = email.trim().toLowerCase();
      const cleanPhone = normalizePhone(phone);

      const currentUserId = localStorage.getItem("userId");
      if (!currentUserId) {
        throw new Error("Unable to determine current user. Please log in again.");
      }

      const response = await api.post("/users/create-user", {
        name: trimmedName,
        email: cleanEmail,
        tempPassword: password,
        phoneNo: cleanPhone,
        role,
        createdBy: currentUserId,
      });

      const data = response.data;
      if (!data?.success) {
        throw new Error(data?.message || "Failed to create user");
      }

      const createdId = data?.data?.userId || data?.data?.user?.userId;
      if (createdId) {
        onCreated?.(createdId, cleanEmail, role);
      }
      onClose();
    } catch (submissionError: any) {
      const message =
        submissionError?.response?.data?.message ||
        submissionError?.message ||
        "Failed to create user.";
      setError(message);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onCancel={onClose}
      title={title}
      hideButtons
      zIndex={6000}
      description={
        description ??
        "Fill out the details below to create a new account. Passwords can be shared directly with the user."
      }
    >
      <div className="modal-form">
        <div className="modal-field-group">
          <label>
            Full Name
            <input
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              placeholder="Firstname Lastname"
            />
          </label>
          {fieldErrors.name && <div className="modal-error">{fieldErrors.name}</div>}
        </div>

        <div className="modal-field-group">
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              placeholder="email@gmail.com"
            />
          </label>
          {fieldErrors.email && <div className="modal-error">{fieldErrors.email}</div>}
        </div>

        {hideTempPasswordField ? (
          <p className="modal-hint">
            A secure temporary password and {firstAllowedRoleLabel} role will be applied automatically.
          </p>
        ) : (
          <div className="modal-field-group">
            <label>
              Temporary Password
              <input value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            {fieldErrors.password && <div className="modal-error">{fieldErrors.password}</div>}
          </div>
        )}

        <div className="modal-field-group">
          <label>
            Phone Number
            <input
              value={phone}
              maxLength={12}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="123-456-7890"
            />
          </label>
          {fieldErrors.phone && <div className="modal-error">{fieldErrors.phone}</div>}
        </div>

        {!hideRoleSelector && allowedRoles.length > 1 && (
          <div className="modal-field-group">
            <label>Role</label>
            <Menu
              items={allowedRoles.map((roleOption) => ({
                key: roleOption.toLowerCase(),
                label: roleOption === "SuperAdmin" ? "Super Admin" : "Operator",
                selected: role === roleOption,
                onSelect: () => setRole(roleOption),
              }))}
              closeOnSelect
              trigger={
                <button type="button" className="menu-trigger modal-menu-trigger">
                  {role === "SuperAdmin" ? "Super Admin" : "Operator"}
                </button>
              }
              dropdownClassName="modal-menu-dropdown"
            />
          </div>
        )}

        {error && <div className="modal-error">{error}</div>}
        <div className="modal-actions-inline">
          <button className="button-secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button className="button-primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create User"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddUserModal;
